import axios from "axios";

import { setAuthorizationHeader } from "utils/auth";
import { getItem, removeItem } from "services/localStorage.service";
import { redirectTo } from "./links";
import { API_DOMAIN, REFRESH_TOKEN_URL } from "./apiConfig";

const axiosInstance = axios.create({
  baseURL: API_DOMAIN,
});

const applyTokenToInstances = (token: string | null) => {
  if (token) {
    axiosInstance.defaults.headers.common["x-auth-token"] = token
    axiosInstance.defaults.headers.common["token"] = token
    axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`
  } else {
    delete axiosInstance.defaults.headers.common["x-auth-token"]
    delete axiosInstance.defaults.headers.common["token"]
    delete axiosInstance.defaults.headers.common["Authorization"]
  }
}

axiosInstance.interceptors.response.use(
  (response) => {
    const token = response.headers["x-auth-token"];
    if (token) {
      setAuthorizationHeader(token);
      applyTokenToInstances(token);
    }
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => response, // Return the response if successful
  async (error) => {
    const originalRequest = error.config;
    if (error?.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Try refreshing the access token
      try {
        const refreshToken = localStorage.getItem("refreshToken"); // Get the refresh token
        const token = localStorage.getItem("x-auth-token"); // Get the refresh token
        if (!REFRESH_TOKEN_URL) {
          throw new Error("Refresh token URL is not configured.");
        }
        if (!refreshToken || !token) {
          throw new Error("Refresh token or access token is missing.");
        }
        const response = await axios.post(REFRESH_TOKEN_URL, {
          refreshToken,
          token,
        });

        // Save the new access token
        const newAccessToken = response.data.data.token;
        const newRefreshToken = response.data.data.refreshToken;
        localStorage.setItem("x-auth-token", newAccessToken);
        if (newRefreshToken) {
          localStorage.setItem("refreshToken", newRefreshToken);
        }

        // Update the Authorization header and retry the original request
        setAuthorizationHeader(newAccessToken);
        applyTokenToInstances(newAccessToken);
        originalRequest.headers["x-auth-token"] = newAccessToken;
        originalRequest.headers["token"] = newAccessToken;
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        // Retry the original request with the new token
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // Handle refresh token failure (e.g., logout user, clear tokens)
        console.error("Refresh token expired or invalid", refreshError);
        localStorage.removeItem("x-auth-token");
        localStorage.removeItem("refreshToken");
        removeItem("persist:root");
        // Redirect to login page or handle unauthorized access
        redirectTo("/login");
      }
    }

    // Return any other errors
    return Promise.reject(error);
  }
);

export default axiosInstance;

// set token from localStorage when application starts
const initialToken = getItem("x-auth-token");
setAuthorizationHeader(initialToken);
applyTokenToInstances(initialToken);
